import React from 'react';
import {
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import useGetCustomerGiftCardBalance from '../../hooks/giftCards/useGetCustomerGiftCardBalance';
import useApplyGiftCardCode from '../../hooks/giftCards/useApplyGiftCardCode';
import { cleanCents, formatDollarAmount } from '../../utils/global/helper';
import {
  formatGiftCardCodeInput,
  getMaxAmountToUse,
  validateGiftCardAmount,
} from '../../helpers/gift-cards';
import { CheckoutCustomer, GiftCardTypeParam } from '../../types/payments';
import { SplitPaymentMethod } from '../../context/SplitPaymentsContext/context';

type Props = {
  isSplitPayment?: boolean;
  splitPaymentPaymentMethods?: SplitPaymentMethod[];
  amountCents: number;
  totalPriceCents: number;
  giftCardType: GiftCardTypeParam;
  customer: CheckoutCustomer;
};

const GENERIC_APPLY_ERROR = 'Failed to apply gift card to customer';

function GiftCardPaymentMethod({
  isSplitPayment = false,
  splitPaymentPaymentMethods,
  amountCents,
  totalPriceCents,
  giftCardType,
  customer,
}: Props) {
  const theme = useTheme();
  const [giftCardCode, setGiftCardCode] = React.useState('');

  const phoneNumber = customer?.phoneNumber;
  const customerId = customer?.customerId;

  const hasIdentifier = !!phoneNumber || !!customerId;

  const {
    data: balanceData,
    isLoading,
    isFetching,
    refetch,
  } = useGetCustomerGiftCardBalance({
    customer,
  });

  const balanceUsed = (splitPaymentPaymentMethods ?? []).reduce(
    (acc, payment) => {
      if (payment.paymentMethod === 'GIFT_CARD') {
        return acc + cleanCents(payment.amount * 100);
      }
      return acc;
    },
    0,
  );

  const applyGiftCardCode = useApplyGiftCardCode({
    onSuccess: async () => {
      toast.success('Gift card has been applied to customer!');
      setGiftCardCode('');
      const { data: { availableBalance = 0 } = {} } = await refetch();
      if (availableBalance - balanceUsed > 0) {
        giftCardType.set('internal');
      }
    },
    onError: (error: Error) => {
      if (error instanceof AxiosError) {
        const isInternalServerError = error.response?.status === 500;
        if (isInternalServerError) {
          toast.error(GENERIC_APPLY_ERROR);
        } else {
          toast.error(error.response?.data.message || GENERIC_APPLY_ERROR);
        }
      }
    },
  });

  const isInternalChecked = giftCardType.value === 'internal';
  const isExternalChecked = giftCardType.value === 'external';

  const availableBalance = (balanceData?.availableBalance ?? 0) - balanceUsed;
  const availableBalanceDollars = availableBalance ? availableBalance / 100 : 0;

  const maxAmountToUse = getMaxAmountToUse({
    isSplitPayment,
    amountCents,
    giftCardBalanceCents: availableBalance ?? 0,
  });

  const amountError = validateGiftCardAmount({
    giftCardType: giftCardType.value,
    amountCents,
    giftCardBalanceCents: availableBalance ?? 0,
    totalPriceCents,
    isSplitPayment,
  }).error;

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatGiftCardCodeInput(e.target.value);
    setGiftCardCode(formatted);
  };

  const handleApplyCode = async () => {
    const cleanedCode = giftCardCode.replace(/[-\s]/g, '');
    if (cleanedCode.length !== 16) {
      toast.error('Gift card code must be 16 characters');
      return;
    }

    applyGiftCardCode.mutate({
      code: cleanedCode,
      customer,
    });
  };

  React.useEffect(() => {
    if (!isFetching && balanceData && balanceData.availableBalance <= 0) {
      giftCardType.set('external');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching, balanceData]);

  React.useEffect(() => {
    // No customer identifier means external is the only option.
    if (!hasIdentifier && giftCardType.value !== 'external') {
      giftCardType.set('external');
    }
  }, [hasIdentifier, giftCardType]);

  if (!hasIdentifier) {
    return (
      <Typography variant="body2" color="text.secondary">
        Recording a non-Porter gift card payment. Porter will not validate the
        balance.
      </Typography>
    );
  }

  if (isLoading && !balanceData) {
    return (
      <Typography variant="body2" color="text.secondary">
        Loading gift card balance...
      </Typography>
    );
  }

  const showBalanceCheckbox = availableBalance > 0;
  const isExternalCheckedDisabled = !showBalanceCheckbox;

  return (
    <Stack
      gap={1.5}
      sx={{
        '& .MuiInputBase-root': {
          height: 'auto',
        },
        '& .MuiInputLabel-root': {
          fontSize: theme.typography.body2.fontSize,
        },
        '& .MuiInputBase-input': {
          fontSize: theme.typography.body2.fontSize,
        },
        '& .MuiFormHelperText-root': {
          textAlign: 'left',
          marginLeft: 0,
        },
      }}>
      {/* Existing Balance Checkbox */}
      {showBalanceCheckbox && (
        <FormControl error={!!amountError} fullWidth>
          <FormControlLabel
            control={
              <Checkbox
                checked={isInternalChecked}
                onChange={() => {
                  giftCardType.set(isInternalChecked ? 'external' : 'internal');
                }}
              />
            }
            label={
              <Typography
                variant="body2"
                component="span"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Use {formatDollarAmount(maxAmountToUse / 100, true)} of the
                client&apos;s{' '}
                {formatDollarAmount(availableBalanceDollars, true)} gift card
                balance
              </Typography>
            }
          />
          {amountError && <FormHelperText error>{amountError}</FormHelperText>}
        </FormControl>
      )}

      {/* Divider with "OR" text */}
      {showBalanceCheckbox && (
        <Divider sx={{ my: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
            OR
          </Typography>
        </Divider>
      )}

      {/* Gift Card Code Input */}
      <Stack gap={1}>
        <Typography variant="body2" color="text.secondary">
          Add a new gift card to customer account
        </Typography>
        <TextField
          size="small"
          label="Gift Card Code"
          placeholder="XXXX-XXXX-XXXX-XXXX"
          value={giftCardCode}
          onChange={handleCodeChange}
          disabled={applyGiftCardCode.isPending}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleApplyCode}
                    disabled={
                      !giftCardCode ||
                      giftCardCode.replace(/[-\s]/g, '').length !== 16 ||
                      applyGiftCardCode.isPending
                    }>
                    {applyGiftCardCode.isPending ? 'Applying...' : 'Apply'}
                  </Button>
                </InputAdornment>
              ),
              inputProps: {
                maxLength: 19, // 16 characters + 3 hyphens
                style: { textTransform: 'uppercase' },
              },
            },
          }}
          helperText="Enter a 16-character gift card code"
          sx={{
            mb: 0,
            maxWidth: 'min(100%, 380px)',
          }}
        />
      </Stack>

      {/* External Gift Card */}
      <FormControl error={!!amountError} fullWidth>
        <FormControlLabel
          control={
            <Checkbox
              checked={isExternalChecked}
              onChange={() => {
                if (isExternalCheckedDisabled) return;
                giftCardType.set(isExternalChecked ? 'internal' : 'external');
              }}
            />
          }
          label={
            <Typography
              variant="body2"
              component="span"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              Record non-Porter gift card payment (Just for your tracking.
              Porter will not validate balances.)
            </Typography>
          }
        />
      </FormControl>
    </Stack>
  );
}

export default GiftCardPaymentMethod;
